import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import WeeklyReportCard from '../components/WeeklyReportCard';
import PayoutsTile from '../components/PayoutsTile';
import { 
  TrendingUp, 
  TrendingDown, 
  DollarSign, 
  Receipt, 
  AlertCircle,
  ArrowRight,
  Upload,
  Sparkles,
  ShieldAlert,
  Clock,
  FileWarning,
  Users,
  CalendarClock
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from 'recharts';

const DashboardPage = () => {
  const { currentLocation, api, seedDemoData, fetchLocations, locations } = useAuth();
  const [stats, setStats] = useState(null);
  const [pl, setPL] = useState(null);
  const [expiringDocs, setExpiringDocs] = useState([]);
  const [todayRota, setTodayRota] = useState(null);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (currentLocation) {
      fetchDashboard();
    } else {
      setLoading(false);
    }
  }, [currentLocation]);

  const fetchDashboard = async () => {
    setLoading(true);
    try {
      const [statsRes, plRes, docsRes, rotaRes] = await Promise.all([
        api.get(`/dashboard/${currentLocation.id}`),
        api.get(`/pl/${currentLocation.id}`).catch(() => ({ data: null })),
        api.get('/documents/expiring', { params: { days: 90 } }).catch(() => ({ data: [] })),
        api.get('/schedule/today', { params: { location_id: currentLocation.id } }).catch(() => ({ data: null }))
      ]);
      setStats(statsRes.data);
      setPL(plRes.data);
      setExpiringDocs(docsRes.data || []);
      setTodayRota(rotaRes.data);
    } catch (err) {
      console.error('Error fetching dashboard:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSeedData = async () => {
    setSeeding(true);
    try {
      const result = await seedDemoData();
      // seedDemoData already fetches locations and sets currentLocation
      // Wait a moment for state to update, then refresh
      setTimeout(() => {
        setSeeding(false);
        // The currentLocation will be set by seedDemoData, 
        // which will trigger the useEffect to fetchDashboard
      }, 500);
    } catch (err) {
      console.error('Error seeding data:', err);
      setSeeding(false);
    }
  };

  // Show setup prompt if no location
  if (!currentLocation && !loading) {
    return (
      <div className="p-6 lg:p-8 max-w-7xl mx-auto">
        <Card className="border-dashed">
          <CardHeader className="text-center pb-2">
            <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Sparkles className="w-8 h-8 text-primary" />
            </div>
            <CardTitle className="text-2xl">Welcome to Tillytics</CardTitle>
            <CardDescription className="text-base max-w-md mx-auto">
              Get started by setting up your first location with demo data.
            </CardDescription>
          </CardHeader>
          <CardContent className="flex justify-center pb-8">
            <Button 
              size="lg" 
              onClick={handleSeedData} 
              disabled={seeding}
              data-testid="seed-demo-data-btn"
            >
              {seeding ? 'Setting up...' : 'Load Demo Data'}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-6 lg:p-8 max-w-7xl mx-auto space-y-6">
        <Skeleton className="h-8 w-64" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[...Array(4)].map((_, i) => (
            <Skeleton key={i} className="h-32" />
          ))}
        </div>
        <Skeleton className="h-80" />
      </div>
    );
  }

  const formatCurrency = (val) => `£${val?.toLocaleString('en-GB', { minimumFractionDigits: 2 }) || '0.00'}`;
  
  const revenueChartData = stats?.recent_revenue?.map(r => ({
    date: new Date(r.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }),
    revenue: r.amount
  })) || [];

  return (
    <div className="p-6 lg:p-8 max-w-7xl mx-auto space-y-6" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">{currentLocation?.name}</h1>
          <p className="text-muted-foreground text-sm sm:text-base">{currentLocation?.address}</p>
        </div>
        <div className="flex gap-2">
          {/* Desktop buttons with text */}
          <Button variant="outline" onClick={() => navigate('/revenue')} data-testid="upload-revenue-btn" className="hidden sm:inline-flex">
            <Upload className="w-4 h-4 mr-2" />
            Upload POS Report
          </Button>
          <Button variant="outline" onClick={() => navigate('/expenses')} data-testid="upload-receipt-btn" className="hidden sm:inline-flex">
            <Receipt className="w-4 h-4 mr-2" />
            Upload Receipt
          </Button>
          {/* Mobile icon buttons */}
          <Button variant="outline" size="icon" onClick={() => navigate('/revenue')} className="sm:hidden" title="Upload POS Report">
            <Upload className="w-4 h-4" />
          </Button>
          <Button variant="outline" size="icon" onClick={() => navigate('/expenses')} className="sm:hidden" title="Upload Receipt">
            <Receipt className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Payouts Tile — POS-agnostic, adapts to connection state */}
        <PayoutsTile
          api={api}
          locationId={currentLocation.id}
          onReconnect={async () => {
            try {
              const res = await api.post('/square/connect', { location_id: currentLocation.id });
              window.location.href = res.data.authorization_url;
            } catch (err) {
              // eslint-disable-next-line no-console
              console.error(err);
            }
          }}
        />
        {/* Revenue Card */}
        <Card className="card-hover">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Monthly Revenue
            </CardTitle>
            <DollarSign className="w-4 h-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold mono-num" data-testid="revenue-display">
              {formatCurrency(stats?.current_month?.revenue)}
            </div>
            <div className="flex items-center gap-1 mt-1">
              {stats?.revenue_change_pct >= 0 ? (
                <TrendingUp className="w-4 h-4 text-profit" />
              ) : (
                <TrendingDown className="w-4 h-4 text-loss" />
              )}
              <span className={`text-sm font-medium ${stats?.revenue_change_pct >= 0 ? 'text-profit' : 'text-loss'}`}>
                {Math.abs(stats?.revenue_change_pct || 0).toFixed(1)}%
              </span>
              <span className="text-sm text-muted-foreground">vs last month</span>
            </div>
          </CardContent>
        </Card>

        {/* Expenses Card */}
        <Card className="card-hover">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Monthly Expenses
            </CardTitle>
            <Receipt className="w-4 h-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold mono-num" data-testid="expenses-display">
              {formatCurrency((pl?.cost_of_goods_sold || 0) + (pl?.labor_costs || 0) + (pl?.operating_expenses || 0))}
            </div>
            <p className="text-sm text-muted-foreground mt-1">
              COGS + Labour + Operating
            </p>
          </CardContent>
        </Card>

        {/* Profit Card */}
        <Card className="card-hover">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Net Profit
            </CardTitle>
            {(pl?.net_profit || 0) >= 0 ? (
              <TrendingUp className="w-4 h-4 text-profit" />
            ) : (
              <TrendingDown className="w-4 h-4 text-loss" />
            )}
          </CardHeader>
          <CardContent>
            <div className={`text-2xl font-bold mono-num ${(pl?.net_profit || 0) >= 0 ? 'text-profit' : 'text-loss'}`} data-testid="profit-display">
              {formatCurrency(pl?.net_profit)}
            </div>
            <p className="text-sm text-muted-foreground mt-1">
              {pl?.net_margin_pct?.toFixed(1) || 0}% margin
            </p>
          </CardContent>
        </Card>

        {/* Pending Card */}
        <Card className="card-hover">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Pending Review
            </CardTitle>
            <AlertCircle className="w-4 h-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold" data-testid="pending-display">
              {stats?.pending_expenses || 0}
            </div>
            <p className="text-sm text-muted-foreground mt-1">
              Expenses need confirmation
            </p>
            {stats?.pending_expenses > 0 && (
              <Button 
                variant="link" 
                className="px-0 h-auto mt-1"
                onClick={() => navigate('/expenses')}
              >
                Review now <ArrowRight className="w-3 h-3 ml-1" />
              </Button>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Revenue Chart */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Revenue Trend</CardTitle>
            <CardDescription>Daily revenue over the last 7 days</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-64" data-testid="revenue-chart">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={revenueChartData}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis 
                    dataKey="date" 
                    tick={{ fontSize: 12 }}
                    tickLine={false}
                    axisLine={false}
                  />
                  <YAxis 
                    tick={{ fontSize: 12 }}
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={(val) => `£${val}`}
                  />
                  <Tooltip 
                    formatter={(value) => [`£${value.toFixed(2)}`, 'Revenue']}
                    contentStyle={{ 
                      borderRadius: '8px',
                      border: '1px solid hsl(var(--border))',
                      boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
                    }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="revenue" 
                    stroke="hsl(var(--chart-1))"
                    strokeWidth={2}
                    dot={{ fill: 'hsl(var(--chart-1))', strokeWidth: 0, r: 4 }}
                    activeDot={{ r: 6 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* P&L Summary */}
        <Card>
          <CardHeader>
            <CardTitle>P&L Summary</CardTitle>
            <CardDescription>Current month breakdown</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex justify-between items-center py-2 border-b">
              <span className="text-muted-foreground">Revenue</span>
              <span className="font-medium mono-num">{formatCurrency(pl?.total_revenue)}</span>
            </div>
            <div className="flex justify-between items-center py-2 border-b">
              <span className="text-muted-foreground">Cost of Goods</span>
              <span className="font-medium mono-num text-loss">-{formatCurrency(pl?.cost_of_goods_sold)}</span>
            </div>
            <div className="flex justify-between items-center py-2 border-b">
              <span className="text-muted-foreground">Gross Profit</span>
              <span className="font-semibold mono-num">{formatCurrency(pl?.gross_profit)}</span>
            </div>
            <div className="flex justify-between items-center py-2 border-b">
              <span className="text-muted-foreground">Labour</span>
              <span className="font-medium mono-num text-loss">-{formatCurrency(pl?.labor_costs)}</span>
            </div>
            <div className="flex justify-between items-center py-2 border-b">
              <span className="text-muted-foreground">Operating</span>
              <span className="font-medium mono-num text-loss">-{formatCurrency(pl?.operating_expenses)}</span>
            </div>
            <div className="flex justify-between items-center py-2 bg-muted/50 -mx-6 px-6 -mb-6 rounded-b-md">
              <span className="font-semibold">Net Profit</span>
              <span className={`font-bold mono-num ${(pl?.net_profit || 0) >= 0 ? 'text-profit' : 'text-loss'}`}>
                {formatCurrency(pl?.net_profit)}
              </span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Weekly AI Report */}
      <WeeklyReportCard />

      {/* Document Compliance Widget */}
      {expiringDocs.length > 0 && (
        <Card 
          className={`border-l-4 ${
            expiringDocs.some(d => d.status === 'expired') 
              ? 'border-l-red-500' 
              : expiringDocs.some(d => d.status === 'expiring_soon') 
                ? 'border-l-amber-500' 
                : 'border-l-blue-500'
          }`}
          data-testid="document-expiry-widget"
        >
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <ShieldAlert className={`w-5 h-5 ${
                  expiringDocs.some(d => d.status === 'expired') ? 'text-red-500' : 'text-amber-500'
                }`} />
                <CardTitle className="text-base">Document Compliance</CardTitle>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/documents')}
                data-testid="view-all-documents-btn"
              >
                View all <ArrowRight className="w-3 h-3 ml-1" />
              </Button>
            </div>
            <div className="flex gap-3 mt-1">
              {(() => {
                const expired = expiringDocs.filter(d => d.status === 'expired').length;
                const soon = expiringDocs.filter(d => d.status === 'expiring_soon').length;
                const later = expiringDocs.filter(d => d.status === 'expiring').length;
                return (
                  <>
                    {expired > 0 && (
                      <span className="text-xs font-medium text-red-600 bg-red-50 dark:bg-red-950/30 px-2 py-0.5 rounded-full" data-testid="expired-count">
                        {expired} expired
                      </span>
                    )}
                    {soon > 0 && (
                      <span className="text-xs font-medium text-amber-600 bg-amber-50 dark:bg-amber-950/30 px-2 py-0.5 rounded-full" data-testid="expiring-soon-count">
                        {soon} expiring soon
                      </span>
                    )}
                    {later > 0 && (
                      <span className="text-xs font-medium text-blue-600 bg-blue-50 dark:bg-blue-950/30 px-2 py-0.5 rounded-full" data-testid="expiring-count">
                        {later} upcoming
                      </span>
                    )}
                  </>
                );
              })()}
            </div>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="space-y-2">
              {expiringDocs.slice(0, 3).map((doc) => {
                const daysLeft = doc.expiry_date 
                  ? Math.ceil((new Date(doc.expiry_date) - new Date()) / (1000 * 60 * 60 * 24))
                  : null;
                const isExpired = daysLeft !== null && daysLeft < 0;
                const isSoon = daysLeft !== null && daysLeft >= 0 && daysLeft <= 30;
                
                return (
                  <div 
                    key={doc.id} 
                    className="flex items-center justify-between py-2 border-b last:border-0"
                    data-testid={`expiry-doc-${doc.id}`}
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      {isExpired ? (
                        <FileWarning className="w-4 h-4 text-red-500 shrink-0" />
                      ) : (
                        <Clock className={`w-4 h-4 shrink-0 ${isSoon ? 'text-amber-500' : 'text-blue-500'}`} />
                      )}
                      <span className="text-sm truncate">{doc.title}</span>
                    </div>
                    <span className={`text-xs font-medium whitespace-nowrap ml-2 ${
                      isExpired ? 'text-red-600' : isSoon ? 'text-amber-600' : 'text-muted-foreground'
                    }`}>
                      {isExpired 
                        ? `${Math.abs(daysLeft)}d overdue` 
                        : daysLeft === 0 
                          ? 'Expires today' 
                          : `${daysLeft}d left`
                      }
                    </span>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Today's Rota Widget */}
      <Card className="border-l-4 border-l-teal-500" data-testid="todays-rota-widget">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Users className="w-5 h-5 text-teal-600" />
              <CardTitle className="text-base">Today's Rota</CardTitle>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/staff')}
              data-testid="view-schedule-btn"
            >
              Full schedule <ArrowRight className="w-3 h-3 ml-1" />
            </Button>
          </div>
          {todayRota && todayRota.total_staff > 0 && (
            <div className="flex gap-3 mt-1">
              <span className="text-xs font-medium text-teal-600 bg-teal-50 dark:bg-teal-950/30 px-2 py-0.5 rounded-full" data-testid="rota-staff-count">
                {todayRota.total_staff} staff
              </span>
              <span className="text-xs font-medium text-muted-foreground bg-muted px-2 py-0.5 rounded-full">
                {todayRota.total_hours}h scheduled
              </span>
              {todayRota.total_cost > 0 && (
                <span className="text-xs font-medium text-muted-foreground bg-muted px-2 py-0.5 rounded-full">
                  est. £{todayRota.total_cost.toFixed(0)}
                </span>
              )}
            </div>
          )}
        </CardHeader>
        <CardContent className="pt-0">
          {!todayRota || todayRota.shifts.length === 0 ? (
            <div className="text-center py-4">
              <CalendarClock className="w-8 h-8 text-muted-foreground/40 mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">No shifts scheduled today</p>
              <Button
                variant="link"
                size="sm"
                onClick={() => navigate('/staff')}
                className="mt-1 text-teal-600"
                data-testid="add-shifts-btn"
              >
                Add shifts →
              </Button>
            </div>
          ) : (
            <div className="space-y-2">
              {todayRota.shifts.slice(0, 5).map((s, idx) => (
                <div
                  key={idx}
                  className="flex items-center justify-between py-2 border-b last:border-0"
                  data-testid={`rota-shift-${idx}`}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <div className="w-2 h-2 rounded-full bg-teal-500 shrink-0" />
                    <span className="text-sm font-medium truncate">{s.employee_name}</span>
                    {s.role && (
                      <span className="text-xs text-muted-foreground hidden sm:inline">({s.role})</span>
                    )}
                  </div>
                  <span className="text-sm text-muted-foreground whitespace-nowrap ml-2">
                    {s.start_time}–{s.end_time}
                  </span>
                </div>
              ))}
              {todayRota.shifts.length > 5 && (
                <p className="text-xs text-muted-foreground text-center pt-1">
                  +{todayRota.shifts.length - 5} more
                </p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle>Quick Actions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Button 
              variant="outline" 
              className="h-auto py-4 flex-col gap-2"
              onClick={() => navigate('/revenue')}
              data-testid="quick-upload-toast"
            >
              <Upload className="w-5 h-5" />
              <span>Upload POS Report</span>
            </Button>
            <Button 
              variant="outline" 
              className="h-auto py-4 flex-col gap-2"
              onClick={() => navigate('/expenses')}
              data-testid="quick-upload-receipt"
            >
              <Receipt className="w-5 h-5" />
              <span>Upload Receipt</span>
            </Button>
            <Button 
              variant="outline" 
              className="h-auto py-4 flex-col gap-2"
              onClick={() => navigate('/ai-chat')}
              data-testid="quick-ask-ai"
            >
              <Sparkles className="w-5 h-5" />
              <span>Ask AI Advisor</span>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default DashboardPage;
